import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import type { AnalyticsEvent } from '@/analytics/events';
import { SWLoadingButton } from '@/components/LoadingButton';
import { SWLoadingButtonOutlined } from '@/components/SWLoadingButtonOutlined';
import { usePmsApiEnabled } from '@/hooks/usePmsApiEnabled';
import { usePmsBetaEnabled } from '@/hooks/usePmsBetaEnabled';
import { useSubscribers } from '@/Structures/hooks/useSubscribers';
import type {
  AppfolioConfig,
  PmsConfig,
  PmsType,
  RentManagerConfig,
  ResmanConfig,
  StructureBilling,
} from '@/Structures/types';
import { PMS_PROVIDERS } from '@/Structures/types';
import { AppfolioMatchingDialog } from '@/Structures/View/components/AppfolioMatchingDialog';
import type { RmChargePayload, RmOccupant } from '@/Structures/View/components/pmsApi';
import { listRmOccupants, uploadChargesToRm } from '@/Structures/View/components/pmsApi';
import {
  buildUnitAppfolioOccupancyIds,
  buildUnitAppfolioOccupancyUids,
  buildUnitAppfolioTenantNames,
  buildUnitOverlappingTenantNames,
  buildUnitResidentNames,
} from '@/Structures/View/components/pmsUtils';
import { ResmanConfigForm } from '@/Structures/View/components/ResmanConfigForm';
import { RmMatchingDialog } from '@/Structures/View/components/RmMatchingDialog';
import type { Unit as StructureUnit } from '@/Units/types';
import { formatNumber, naturalCompare } from '@/util';

import type { CustomInvoiceLineItem } from '../api';
import type { PersistedInvoiceContext } from '../pmsExport';
import {
  buildRentManagerChargePayloads,
  buildUnitChargeBreakdown,
  detectMidCycleChanges,
  downloadAppfolioCsv,
  downloadRentManagerCsv,
  downloadResmanCsv,
} from '../pmsExport';

interface PmsActionsProps {
  bill: StructureBilling;
  localInvoices: any[];
  tz: string;
  structureName: string;
  eventDetails: Record<string, any>;
  trackEvent: (event: AnalyticsEvent, properties?: Record<string, any>) => void;
  pmsType?: PmsType | null;
  pmsConfig?: PmsConfig | null;
  unitPmsNames?: Record<string, string>;
  /** RM uses integer UnitIDs; AppFolio uses string IDs (may include a `#` prefix). */
  unitPmsIds?: Record<string, number | string>;
  onPmsSettingsUpdate: (pmsType: PmsType | null, pmsConfig: PmsConfig | null) => Promise<void>;
  structureUnits?: StructureUnit[];
  onStructureRefresh?: () => void;
  // The three custom-invoice maps from the download-urls polling response (state owned by
  // BreakdownDialog, same maps RenderTable/handleCsvDownload use). When present, PMS export
  // rows use each unit's persisted amount_due_cents instead of a live recomputation; see
  // `resolveUnitPmsAmount` in pmsExport.ts.
  customInvoiceAmountCents?: Map<string, number>;
  customInvoiceBreakdown?: Map<
    string,
    { usageCents: number; commonAreaCents: number; feeCents: number }
  >;
  customInvoiceLineItems?: Map<string, CustomInvoiceLineItem[]>;
}

/**
 * Compute unit_cost (fee portion) for each unit.
 * Same logic as mergeInvoicesToUnits in BreakdownDialog but returns UnitBilling[].
 */
function enrichBillUnits(bill: StructureBilling) {
  const lineItems = bill.line_items ?? [];
  const splitItems = lineItems.filter((item) => item.type === 'SPLIT');
  const flatItems = lineItems.filter((item) => item.type === 'FLAT');
  const unitFlatItems = lineItems.filter((item) => item.type === 'UNIT_FLAT');
  const billableUnitsCount = bill.billable_units_count ?? bill.units.length;
  const commonAreaCostPerUnit = (bill.common_area_usage ?? []).reduce(
    (sum, ca) => sum + ca.split_cost,
    0,
  );

  return bill.units.map((unit) => {
    let unitCost = 0;
    const isBillable = unit.is_billable ?? true;

    if (isBillable) {
      unitCost += commonAreaCostPerUnit;
      splitItems.forEach((item) => {
        unitCost += (item.cost ?? 0) / billableUnitsCount;
      });
      flatItems.forEach((item) => {
        unitCost += item.cost ?? 0;
      });
    }

    unitFlatItems.forEach((item) => {
      if (item.unit_id === unit.id) {
        unitCost += item.cost ?? 0;
      }
    });

    return { ...unit, unit_cost: unitCost };
  });
}

export const PmsActions = ({
  bill,
  localInvoices,
  tz,
  structureName,
  eventDetails,
  trackEvent,
  pmsType,
  pmsConfig,
  unitPmsNames,
  unitPmsIds,
  onPmsSettingsUpdate,
  structureUnits,
  onStructureRefresh,
  customInvoiceAmountCents = new Map(),
  customInvoiceBreakdown = new Map(),
  customInvoiceLineItems = new Map(),
}: PmsActionsProps): JSX.Element => {
  const enrichedUnits = useMemo(() => enrichBillUnits(bill), [bill]);

  // Bundled for the pmsExport writers: lets them prefer each unit's persisted invoice
  // amount over a live recomputation once custom invoices have been generated for the bill.
  const persistedInvoiceContext = useMemo<PersistedInvoiceContext>(
    () => ({
      localInvoices,
      customInvoiceAmountCents,
      customInvoiceBreakdown,
      customInvoiceLineItems,
    }),
    [localInvoices, customInvoiceAmountCents, customInvoiceBreakdown, customInvoiceLineItems],
  );

  // Per-provider narrowing of pmsConfig.
  const rmPmsConfig = pmsType === 'RENT_MANAGER' ? (pmsConfig as RentManagerConfig | null) : null;
  const appfolioPmsConfig = pmsType === 'APPFOLIO' ? (pmsConfig as AppfolioConfig | null) : null;
  const resmanPmsConfig = pmsType === 'RESMAN' ? (pmsConfig as ResmanConfig | null) : null;

  // RM uses integer UnitIDs; AppFolio uses strings. Narrow the map per-provider when needed.
  const rmUnitPmsIds = useMemo<Record<string, number>>(() => {
    if (pmsType !== 'RENT_MANAGER' || !unitPmsIds) return {};
    const out: Record<string, number> = {};
    Object.entries(unitPmsIds).forEach(([k, v]) => {
      if (typeof v === 'number') out[k] = v;
    });
    return out;
  }, [pmsType, unitPmsIds]);

  const appfolioUnitPmsIds = useMemo<Record<string, string>>(() => {
    if (pmsType !== 'APPFOLIO' || !unitPmsIds) return {};
    const out: Record<string, string> = {};
    Object.entries(unitPmsIds).forEach(([k, v]) => {
      out[k] = String(v);
    });
    return out;
  }, [pmsType, unitPmsIds]);

  // AppFolio also carries Occupancy UID / Occupancy ID / Tenant Name on each unit, captured
  // at matching time from the Bulk Charges template. These come from structureUnits[i].pms_config.
  const appfolioOccupancyUids = useMemo<Record<string, string>>(
    () =>
      pmsType === 'APPFOLIO' && structureUnits
        ? buildUnitAppfolioOccupancyUids(structureUnits)
        : {},
    [pmsType, structureUnits],
  );
  const appfolioOccupancyIds = useMemo<Record<string, string>>(
    () =>
      pmsType === 'APPFOLIO' && structureUnits ? buildUnitAppfolioOccupancyIds(structureUnits) : {},
    [pmsType, structureUnits],
  );
  const appfolioTenantNames = useMemo<Record<string, string>>(
    () =>
      pmsType === 'APPFOLIO' && structureUnits ? buildUnitAppfolioTenantNames(structureUnits) : {},
    [pmsType, structureUnits],
  );

  // Subscribers feed two provider-specific features: ResMan's "Resident Name" auto-fill in
  // its CSV export, and the Rent Manager upload confirmation's SimpleSUB-side tenant
  // context (shown next to RM's occupant lookup so the PM can see when SS and RM disagree,
  // e.g., post-turnover state RM hasn't been updated for or prorated multi-tenant units
  // RM's single-occupant model can't represent). Fetched only for those two providers to
  // avoid the request on AppFolio / unconfigured flows that don't need it.
  const { subscribers } = useSubscribers(
    pmsType === 'RESMAN' || pmsType === 'RENT_MANAGER' ? bill.structure_id : '',
  );
  const resmanUnitResidentNames = useMemo<Record<string, string>>(() => {
    if (pmsType !== 'RESMAN') return {};
    return buildUnitResidentNames(subscribers, { epochSeconds: bill.end_time, timeZone: tz });
  }, [pmsType, subscribers, bill.end_time, tz]);

  // SS unit IDs with more than one invoice for this billing period. Multiple
  // invoices implies a mid-cycle turnover or co-tenant split, both of which
  // produce per-tenant prorated amounts in SimpleSUB. Rent Manager's API is
  // per-unit single-tenant: one charge per UnitID, billed to the current
  // occupant. Uploading the un-prorated unit total here would silently
  // overcharge whoever RM lists as current (the other tenant gets nothing).
  // The upload flow excludes these units from the actual upload and surfaces
  // them in the confirmation dialog with a skip reason; PM bills manually.
  const midCycleUnitIds = useMemo<Set<string>>(() => {
    if (pmsType !== 'RENT_MANAGER') return new Set();
    const ids = new Set<string>();
    for (const unit of enrichedUnits) {
      const count = localInvoices.filter(
        (inv: any) => (inv.unitId ?? inv.unit_id) === unit.id,
      ).length;
      if (count > 1) ids.add(unit.id);
    }
    return ids;
  }, [pmsType, enrichedUnits, localInvoices]);

  // Per SS unit, the list of "First Last" names whose occupancy window overlaps the
  // billing period. Used in the upload confirmation row tenant cell to surface SS-side
  // ground truth alongside RM's occupant lookup. Mirrors the simple overlap check from
  // BillingManager.get_valid_billing_contacts without the co-tenant grouping/winners
  // step: this is informational, not a billing decision, so we want every tenant who
  // overlapped (including both sides of a mid-cycle turnover).
  const ssTenantsByUnit = useMemo<Record<string, string[]>>(() => {
    if (pmsType !== 'RENT_MANAGER') return {};
    if (!bill.start_time || !bill.end_time) return {};
    return buildUnitOverlappingTenantNames(subscribers, {
      startEpochSeconds: bill.start_time,
      endEpochSeconds: bill.end_time,
      timeZone: tz,
    });
  }, [pmsType, subscribers, bill.start_time, bill.end_time, tz]);

  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  // Dialog state
  const [showPmsPickerDialog, setShowPmsPickerDialog] = useState(false);
  const [pickerPmsType, setPickerPmsType] = useState<PmsType | ''>('');
  const [showRmMatchingForSetup, setShowRmMatchingForSetup] = useState(false);
  const [showRmMatchingForUpload, setShowRmMatchingForUpload] = useState(false);
  const [showPmsSetupDialog, setShowPmsSetupDialog] = useState(false);
  const [showAppfolioSetup, setShowAppfolioSetup] = useState(false);
  const [showResmanSetup, setShowResmanSetup] = useState(false);
  const [showMidCycleWarning, setShowMidCycleWarning] = useState(false);
  const [midCycleWarnings, setMidCycleWarnings] = useState<
    { unitName: string; invoiceCount: number }[]
  >([]);
  // Captured at trigger time: which provider raised the warning, and what to
  // do if the user clicks Export Anyway. Each per-provider export handler
  // supplies its own `run` closure; the dialog stays provider-agnostic and
  // new PMS providers don't need to touch the dialog or the confirm handler.
  const [pendingMidCycleExport, setPendingMidCycleExport] = useState<{
    pmsType: PmsType;
    run: (wasMidCycle: boolean) => Promise<void>;
  } | null>(null);
  const [pmsSetupPropertyName, setPmsSetupPropertyName] = useState('');
  const [pmsSetupChargeType, setPmsSetupChargeType] = useState('');
  const [pmsSetupUnitMappings, setPmsSetupUnitMappings] = useState<Record<string, string>>({});
  const [pmsSetupSaving, setPmsSetupSaving] = useState(false);

  // Upload confirmation dialog state
  const [showUploadConfirm, setShowUploadConfirm] = useState(false);
  const [uploadPayloads, setUploadPayloads] = useState<RmChargePayload[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [occupants, setOccupants] = useState<Record<string, RmOccupant>>({});
  const [loadingOccupants, setLoadingOccupants] = useState(false);

  // Flag: after saving mappings via "Upload Charges..." flow, auto-open confirmation
  // when the parent re-renders with updated pmsConfig/unitPmsIds
  const pendingUploadAfterSave = useRef(false);

  // When pmsConfig/unitPmsIds update after saving mappings, auto-open upload confirmation
  useEffect(() => {
    if (
      pendingUploadAfterSave.current &&
      rmPmsConfig?.property_id &&
      rmPmsConfig?.charge_type_id &&
      Object.keys(rmUnitPmsIds).length > 0
    ) {
      pendingUploadAfterSave.current = false;
      const payloads = buildRentManagerChargePayloads(
        bill,
        enrichedUnits,
        rmPmsConfig,
        tz,
        rmUnitPmsIds,
        persistedInvoiceContext,
      );
      if (payloads.length > 0) {
        setUploadPayloads(payloads);
        setShowUploadConfirm(true);
        setLoadingOccupants(true);
        listRmOccupants(payloads.map((p) => p.UnitID))
          .then((result) => setOccupants(result))
          .catch(() => setOccupants({}))
          .finally(() => setLoadingOccupants(false));
      } else {
        toast.error('No billable units with RM unit mappings to upload', { closeButton: true });
      }
    }
  }, [rmPmsConfig, rmUnitPmsIds, bill, enrichedUnits, tz, persistedInvoiceContext]);

  // Feature flag: PMS API integration
  const { pmsApiEnabled } = usePmsApiEnabled();
  // Feature flag: gates non-GA (beta) providers in the picker
  const { pmsBetaEnabled } = usePmsBetaEnabled();

  const visiblePickerProviders = useMemo(
    () =>
      (Object.entries(PMS_PROVIDERS) as [PmsType, (typeof PMS_PROVIDERS)[PmsType]][])
        .filter(([, p]) => !p.beta || pmsBetaEnabled)
        .sort(([, a], [, b]) => naturalCompare(a.label, b.label)),
    [pmsBetaEnabled],
  );

  // Capabilities (API features gated by feature flag)
  const provider = pmsType ? PMS_PROVIDERS[pmsType] : null;
  const capabilities = provider?.capabilities ?? [];
  const hasCsvExport = capabilities.includes('csv_export');
  const hasApiUpload = pmsApiEnabled && capabilities.includes('api_upload');
  const isConfigured = !!pmsType && !!pmsConfig;
  const isApiReady = !!rmPmsConfig?.property_id && !!rmPmsConfig?.charge_type_id;

  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(e.currentTarget);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // --- PMS Export Handlers ---

  const performRentManagerExport = useCallback(
    async (config: RentManagerConfig, exportUnitPmsNames: Record<string, string> = {}) => {
      const runDownload = async (wasMidCycle: boolean) => {
        try {
          await downloadRentManagerCsv(
            bill,
            enrichedUnits,
            config,
            tz,
            structureName,
            exportUnitPmsNames,
            persistedInvoiceContext,
          );
          trackEvent('PMS Export Downloaded', {
            ...eventDetails,
            pmsType: 'RENT_MANAGER',
            midCycleWarning: wasMidCycle,
          });
          toast.success('Rent Manager export downloaded', { closeButton: true });
        } catch (err: any) {
          trackEvent('PMS Export Failed', {
            ...eventDetails,
            pmsType: 'RENT_MANAGER',
            error: err?.message,
          });
          toast.error(err?.message ?? 'Rent Manager export failed', { closeButton: true });
        }
      };
      const warnings = detectMidCycleChanges(enrichedUnits, localInvoices);
      if (warnings.length > 0) {
        setMidCycleWarnings(warnings);
        setPendingMidCycleExport({ pmsType: 'RENT_MANAGER', run: runDownload });
        setShowMidCycleWarning(true);
        return;
      }
      await runDownload(false);
    },
    [
      bill,
      enrichedUnits,
      localInvoices,
      tz,
      structureName,
      trackEvent,
      eventDetails,
      persistedInvoiceContext,
    ],
  );

  const performAppfolioExport = useCallback(
    async (
      config: AppfolioConfig,
      exportUnitPmsIds: Record<string, string>,
      exportUnitPmsNames: Record<string, string> = {},
      exportOccupancyUids: Record<string, string> = {},
      exportOccupancyIds: Record<string, string> = {},
      exportTenantNames: Record<string, string> = {},
    ) => {
      const runDownload = async (wasMidCycle: boolean) => {
        try {
          await downloadAppfolioCsv(
            bill,
            enrichedUnits,
            config,
            tz,
            structureName,
            exportUnitPmsIds,
            exportUnitPmsNames,
            exportOccupancyUids,
            exportOccupancyIds,
            exportTenantNames,
            persistedInvoiceContext,
          );
          trackEvent('PMS Export Downloaded', {
            ...eventDetails,
            pmsType: 'APPFOLIO',
            midCycleWarning: wasMidCycle,
          });
          toast.success('AppFolio export downloaded', { closeButton: true });
        } catch (err: any) {
          trackEvent('PMS Export Failed', {
            ...eventDetails,
            pmsType: 'APPFOLIO',
            error: err?.message,
          });
          toast.error(err?.message ?? 'AppFolio export failed', { closeButton: true });
        }
      };
      const warnings = detectMidCycleChanges(enrichedUnits, localInvoices);
      if (warnings.length > 0) {
        setMidCycleWarnings(warnings);
        setPendingMidCycleExport({ pmsType: 'APPFOLIO', run: runDownload });
        setShowMidCycleWarning(true);
        return;
      }
      await runDownload(false);
    },
    [
      bill,
      enrichedUnits,
      localInvoices,
      tz,
      structureName,
      trackEvent,
      eventDetails,
      persistedInvoiceContext,
    ],
  );

  const performResmanExport = useCallback(
    async (
      config: ResmanConfig,
      exportUnitPmsNames: Record<string, string> = {},
      exportUnitResidentNames: Record<string, string> = {},
    ) => {
      const runDownload = async (wasMidCycle: boolean) => {
        try {
          await downloadResmanCsv(
            bill,
            enrichedUnits,
            config,
            tz,
            structureName,
            exportUnitPmsNames,
            exportUnitResidentNames,
            persistedInvoiceContext,
          );
          trackEvent('PMS Export Downloaded', {
            ...eventDetails,
            pmsType: 'RESMAN',
            midCycleWarning: wasMidCycle,
          });
          toast.success('ResMan export downloaded', { closeButton: true });
        } catch (err: any) {
          trackEvent('PMS Export Failed', {
            ...eventDetails,
            pmsType: 'RESMAN',
            error: err?.message,
          });
          toast.error(err?.message ?? 'ResMan export failed', { closeButton: true });
        }
      };
      const warnings = detectMidCycleChanges(enrichedUnits, localInvoices);
      if (warnings.length > 0) {
        setMidCycleWarnings(warnings);
        setPendingMidCycleExport({ pmsType: 'RESMAN', run: runDownload });
        setShowMidCycleWarning(true);
        return;
      }
      await runDownload(false);
    },
    [
      bill,
      enrichedUnits,
      localInvoices,
      tz,
      structureName,
      trackEvent,
      eventDetails,
      persistedInvoiceContext,
    ],
  );

  const handlePmsExport = useCallback(() => {
    handleMenuClose();
    if (pmsType === 'RENT_MANAGER' && rmPmsConfig) {
      performRentManagerExport(rmPmsConfig, unitPmsNames ?? {});
      return;
    }
    if (pmsType === 'APPFOLIO' && appfolioPmsConfig) {
      performAppfolioExport(
        appfolioPmsConfig,
        appfolioUnitPmsIds,
        unitPmsNames ?? {},
        appfolioOccupancyUids,
        appfolioOccupancyIds,
        appfolioTenantNames,
      );
      return;
    }
    if (pmsType === 'RESMAN' && resmanPmsConfig) {
      performResmanExport(resmanPmsConfig, unitPmsNames ?? {}, resmanUnitResidentNames);
      return;
    }
    // No PMS configured (or partially configured): open first-export RM setup dialog (legacy
    // fallback for the existing RM CSV manual flow). AppFolio/ResMan configure through
    // PmsSettings, so this branch shouldn't be reached for them in practice.
    const billableUnits = enrichedUnits.filter((u) => {
      const isBillable = u.is_billable ?? true;
      const flags = u.unit_flags ?? [];
      return isBillable && !flags.includes('COMMON_AREA') && !flags.includes('NON_BILLABLE');
    });
    setPmsSetupPropertyName(structureName);
    setPmsSetupChargeType('');
    const mappings: Record<string, string> = {};
    [...billableUnits]
      .sort((a, b) => naturalCompare(a.name, b.name))
      .forEach((u) => {
        mappings[u.id] = u.name;
      });
    setPmsSetupUnitMappings(mappings);
    setShowPmsSetupDialog(true);
  }, [
    pmsType,
    rmPmsConfig,
    appfolioPmsConfig,
    resmanPmsConfig,
    appfolioUnitPmsIds,
    appfolioOccupancyUids,
    appfolioOccupancyIds,
    appfolioTenantNames,
    resmanUnitResidentNames,
    unitPmsNames,
    performRentManagerExport,
    performAppfolioExport,
    performResmanExport,
    enrichedUnits,
    structureName,
  ]);

  const prepareUploadConfirmation = useCallback(() => {
    if (!rmPmsConfig?.property_id || !rmPmsConfig?.charge_type_id) {
      return false;
    }
    const payloads = buildRentManagerChargePayloads(
      bill,
      enrichedUnits,
      rmPmsConfig,
      tz,
      rmUnitPmsIds,
      persistedInvoiceContext,
    );
    if (payloads.length === 0) {
      toast.error('No billable units with RM unit mappings to upload', { closeButton: true });
      return false;
    }
    setUploadPayloads(payloads);
    setShowUploadConfirm(true);
    setLoadingOccupants(true);
    listRmOccupants(payloads.map((p) => p.UnitID))
      .then((result) => setOccupants(result))
      .catch(() => setOccupants({}))
      .finally(() => setLoadingOccupants(false));
    return true;
  }, [rmPmsConfig, rmUnitPmsIds, bill, enrichedUnits, tz, persistedInvoiceContext]);

  const handleUploadToRm = useCallback(() => {
    handleMenuClose();
    if (!rmPmsConfig?.property_id || !rmPmsConfig?.charge_type_id) {
      setShowRmMatchingForUpload(true);
      return;
    }
    prepareUploadConfirmation();
  }, [rmPmsConfig, prepareUploadConfirmation]);

  const occupiedPayloads = useMemo(
    () => uploadPayloads.filter((p) => occupants[String(p.UnitID)] !== null),
    [uploadPayloads, occupants],
  );

  const vacantCount = uploadPayloads.length - occupiedPayloads.length;

  // Eligible = RM has a tenant AND the unit isn't mid-cycle in SimpleSUB.
  // RM's per-unit single-tenant model can't represent prorated turnover, so
  // sending the un-prorated unit total here would silently overcharge whoever
  // RM lists. Skip these from the upload; PM bills them manually in RM.
  const eligiblePayloads = useMemo(
    () =>
      occupiedPayloads.filter((p) => {
        const ssUnit = enrichedUnits.find((u) => rmUnitPmsIds[u.id] === p.UnitID);
        return ssUnit ? !midCycleUnitIds.has(ssUnit.id) : true;
      }),
    [occupiedPayloads, enrichedUnits, rmUnitPmsIds, midCycleUnitIds],
  );

  // Count of units skipped specifically because of mid-cycle turnover (i.e.,
  // would have been uploaded if RM had a tenant but for the prorated state).
  // Computed against occupiedPayloads so vacant-AND-mid-cycle is counted as
  // vacant (in vacantCount) and not double-counted here.
  const midCycleSkipCount = useMemo(
    () =>
      occupiedPayloads.filter((p) => {
        const ssUnit = enrichedUnits.find((u) => rmUnitPmsIds[u.id] === p.UnitID);
        return ssUnit ? midCycleUnitIds.has(ssUnit.id) : false;
      }).length,
    [occupiedPayloads, enrichedUnits, rmUnitPmsIds, midCycleUnitIds],
  );

  const handleConfirmUpload = useCallback(async () => {
    setIsUploading(true);
    try {
      await uploadChargesToRm(bill.billing_id, eligiblePayloads);
      trackEvent('PMS Charges Uploaded', {
        ...eventDetails,
        pmsType: 'RENT_MANAGER',
        chargeCount: eligiblePayloads.length,
        midCycleSkipped: midCycleSkipCount,
      });
      toast.success(`Uploaded ${eligiblePayloads.length} charge(s) to Rent Manager`, {
        closeButton: true,
      });
      setShowUploadConfirm(false);
    } catch (err: any) {
      trackEvent('PMS Upload Failed', {
        ...eventDetails,
        pmsType: 'RENT_MANAGER',
        chargeCount: eligiblePayloads.length,
        error: err?.message,
      });
      toast.error(`Failed to upload charges: ${err.message}`, { closeButton: true });
    } finally {
      setIsUploading(false);
    }
  }, [bill.billing_id, eligiblePayloads, midCycleSkipCount, trackEvent, eventDetails]);

  const handleMidCycleConfirm = useCallback(async () => {
    if (!pendingMidCycleExport) {
      setShowMidCycleWarning(false);
      return;
    }
    const { run } = pendingMidCycleExport;
    setShowMidCycleWarning(false);
    setPendingMidCycleExport(null);
    await run(true);
  }, [pendingMidCycleExport]);

  const handleMidCycleCancel = useCallback(() => {
    if (pendingMidCycleExport) {
      trackEvent('PMS Mid-Cycle Export Cancelled', {
        ...eventDetails,
        pmsType: pendingMidCycleExport.pmsType,
      });
    }
    setShowMidCycleWarning(false);
    setPendingMidCycleExport(null);
  }, [pendingMidCycleExport, trackEvent, eventDetails]);

  const handlePmsSetupSaveAndExport = useCallback(async () => {
    if (!pmsSetupPropertyName.trim()) {
      toast.error('Property Short Name is required', { closeButton: true });
      return;
    }
    setPmsSetupSaving(true);
    try {
      const config: RentManagerConfig = {
        property_short_name: pmsSetupPropertyName.trim(),
        default_charge_type: pmsSetupChargeType.trim() || undefined,
      };
      await onPmsSettingsUpdate('RENT_MANAGER', config);
      const { updateUnitPmsConfig } = await import('@/Units/api');
      await Promise.all(
        Object.entries(pmsSetupUnitMappings).map(([unitId, pmsName]) =>
          updateUnitPmsConfig(
            unitId,
            pmsName ? 'RENT_MANAGER' : null,
            pmsName ? { unit_name: pmsName } : null,
          ),
        ),
      );
      setShowPmsSetupDialog(false);
      await performRentManagerExport(config, pmsSetupUnitMappings);
    } catch {
      toast.error('Failed to save PMS settings', { closeButton: true });
    } finally {
      setPmsSetupSaving(false);
    }
  }, [
    pmsSetupPropertyName,
    pmsSetupChargeType,
    pmsSetupUnitMappings,
    onPmsSettingsUpdate,
    performRentManagerExport,
  ]);

  const handleSetupClick = () => {
    setPickerPmsType('');
    setShowPmsPickerDialog(true);
  };

  const buttonLabel = isConfigured ? `Send to ${provider!.label}` : 'Set Up PMS...';

  return (
    <>
      {isConfigured ? (
        <>
          <SWLoadingButton onClick={handleMenuOpen} endIcon={<ArrowDropDownIcon />}>
            {buttonLabel}
          </SWLoadingButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {hasCsvExport && (
              <MenuItem onClick={handlePmsExport}>
                Export {provider ? `${provider.label} ` : ''}CSV
              </MenuItem>
            )}
            {hasApiUpload && isApiReady && (
              <MenuItem onClick={handleUploadToRm}>Upload Charges</MenuItem>
            )}
            {hasApiUpload && !isApiReady && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  setShowRmMatchingForUpload(true);
                }}
              >
                Upload Charges...
              </MenuItem>
            )}
          </Menu>
        </>
      ) : (
        <SWLoadingButton onClick={handleSetupClick}>{buttonLabel}</SWLoadingButton>
      )}

      {/* Mid-Cycle Tenant Change Warning Dialog */}
      <Dialog open={showMidCycleWarning} onClose={handleMidCycleCancel}>
        <DialogTitle>
          Mid-Cycle Tenant Changes Detected
          <IconButton
            aria-label="close"
            onClick={handleMidCycleCancel}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The following units had tenant changes during this billing period. The export will
            include one row per unit with the total charge (not a per-tenant split). The current
            occupant on file will receive the full charge.
          </Typography>
          {midCycleWarnings.map((w) => (
            <Typography key={w.unitName} variant="body2" sx={{ ml: 2, mb: 0.5 }}>
              &bull; {w.unitName} ({w.invoiceCount} invoices)
            </Typography>
          ))}
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            You may want to manually adjust charges in{' '}
            {pendingMidCycleExport
              ? PMS_PROVIDERS[pendingMidCycleExport.pmsType]?.label ?? 'your PMS'
              : 'your PMS'}{' '}
            after import.
          </Typography>
        </DialogContent>
        <DialogActions>
          <SWLoadingButtonOutlined onClick={handleMidCycleCancel}>Cancel</SWLoadingButtonOutlined>
          <SWLoadingButton onClick={handleMidCycleConfirm}>Export Anyway</SWLoadingButton>
        </DialogActions>
      </Dialog>

      {/* PMS Type Picker Dialog */}
      <Dialog
        open={showPmsPickerDialog}
        onClose={() => setShowPmsPickerDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Set Up PMS Integration
          <IconButton
            aria-label="close"
            onClick={() => setShowPmsPickerDialog(false)}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Select your Property Management System to get started.
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={pickerPmsType}
            onChange={(e) => {
              const picked = e.target.value as PmsType | '';
              setPickerPmsType(picked);
              if (picked) {
                trackEvent('PMS Provider Selected', {
                  ...eventDetails,
                  pmsType: picked,
                  entry_point: 'billing_dialog',
                });
              }
            }}
            SelectProps={{ native: true }}
          >
            <option value="">Select a PMS...</option>
            {visiblePickerProviders.map(([key, p]) => (
              <option key={key} value={key}>
                {p.label}
                {p.beta ? ' (Beta)' : ''}
              </option>
            ))}
          </TextField>
          {pickerPmsType && (
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              {pmsApiEnabled &&
                PMS_PROVIDERS[pickerPmsType as PmsType]?.capabilities.includes('api_config') && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setShowPmsPickerDialog(false);
                      setShowRmMatchingForSetup(true);
                    }}
                  >
                    Connect to {PMS_PROVIDERS[pickerPmsType as PmsType].label} (Recommended)
                  </Button>
                )}
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setShowPmsPickerDialog(false);
                  if (pickerPmsType === 'APPFOLIO') {
                    setShowAppfolioSetup(true);
                    return;
                  }
                  if (pickerPmsType === 'RESMAN') {
                    setShowResmanSetup(true);
                    return;
                  }
                  // RM manual fallback: pre-fill the legacy property/charge/mappings form
                  const billableUnits = enrichedUnits.filter((u) => {
                    const isBillable = u.is_billable ?? true;
                    const flags = u.unit_flags ?? [];
                    return (
                      isBillable &&
                      !flags.includes('COMMON_AREA') &&
                      !flags.includes('NON_BILLABLE')
                    );
                  });
                  setPmsSetupPropertyName(structureName);
                  setPmsSetupChargeType('');
                  const mappings: Record<string, string> = {};
                  [...billableUnits]
                    .sort((a, b) => naturalCompare(a.name, b.name))
                    .forEach((u) => {
                      mappings[u.id] = u.name;
                    });
                  setPmsSetupUnitMappings(mappings);
                  setShowPmsSetupDialog(true);
                }}
              >
                {pickerPmsType === 'APPFOLIO'
                  ? 'Upload AppFolio Export'
                  : pickerPmsType === 'RESMAN'
                  ? 'Configure ResMan'
                  : 'Configure Manually'}
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* First-Export PMS Setup Dialog (manual text form — fallback for CSV-only PMS) */}
      <Dialog
        open={showPmsSetupDialog}
        onClose={() => !pmsSetupSaving && setShowPmsSetupDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Set Up Rent Manager Export
          <IconButton
            onClick={() => setShowPmsSetupDialog(false)}
            disabled={pmsSetupSaving}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Configure your Rent Manager integration to export billing data. These settings will be
            saved for future exports.
          </Typography>

          {pmsApiEnabled && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Manual configuration only supports CSV exports. To upload charges directly to Rent
              Manager, use &quot;Connect to Rent Manager&quot; instead.
            </Alert>
          )}

          <TextField
            fullWidth
            size="small"
            label="Property Short Name"
            value={pmsSetupPropertyName}
            onChange={(e) => setPmsSetupPropertyName(e.target.value)}
            helperText="Must match the Property Short Name in Rent Manager"
            disabled={pmsSetupSaving}
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            size="small"
            label="Default Charge Type (optional)"
            value={pmsSetupChargeType}
            onChange={(e) => setPmsSetupChargeType(e.target.value)}
            helperText="Leave blank to set during Rent Manager import"
            disabled={pmsSetupSaving}
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Unit Name Mappings
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Map SimpleSub unit names to Rent Manager unit names. Pre-filled with SimpleSub names.
          </Typography>
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>SimpleSub Unit Name</TableCell>
                  <TableCell>Rent Manager Unit Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(pmsSetupUnitMappings).map(([unitId, rmName]) => {
                  const unit = bill.units.find((u) => u.id === unitId);
                  return (
                    <TableRow key={unitId}>
                      <TableCell>
                        <Typography variant="body2">{unit?.name || unitId}</Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={rmName}
                          onChange={(e) =>
                            setPmsSetupUnitMappings((prev) => ({
                              ...prev,
                              [unitId]: e.target.value,
                            }))
                          }
                          disabled={pmsSetupSaving}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SWLoadingButtonOutlined
            onClick={() => setShowPmsSetupDialog(false)}
            disabled={pmsSetupSaving}
          >
            Cancel
          </SWLoadingButtonOutlined>
          <SWLoadingButton onClick={handlePmsSetupSaveAndExport} loading={pmsSetupSaving}>
            Save &amp; Export
          </SWLoadingButton>
        </DialogActions>
      </Dialog>

      {/* RM Matching Dialog — initial setup */}
      {structureUnits && (
        <RmMatchingDialog
          open={showRmMatchingForSetup}
          onClose={() => setShowRmMatchingForSetup(false)}
          units={structureUnits}
          onUpdate={
            onPmsSettingsUpdate as (
              pmsType: PmsType | null,
              pmsConfig: RentManagerConfig | null,
            ) => Promise<void>
          }
          onRefresh={onStructureRefresh}
          existingConfig={rmPmsConfig}
        />
      )}

      {/* RM Matching Dialog — upload (requires charge type) */}
      {structureUnits && (
        <RmMatchingDialog
          open={showRmMatchingForUpload}
          onClose={() => setShowRmMatchingForUpload(false)}
          units={structureUnits}
          onUpdate={
            onPmsSettingsUpdate as (
              pmsType: PmsType | null,
              pmsConfig: RentManagerConfig | null,
            ) => Promise<void>
          }
          onRefresh={onStructureRefresh}
          existingConfig={rmPmsConfig}
          requireChargeType
          onSaveSuccess={() => {
            // Flag so the effect opens the upload confirmation after props update
            pendingUploadAfterSave.current = true;
          }}
        />
      )}

      {/* AppFolio Matching Dialog: first-export setup from BreakdownDialog */}
      {structureUnits && (
        <AppfolioMatchingDialog
          open={showAppfolioSetup}
          onClose={() => setShowAppfolioSetup(false)}
          units={structureUnits}
          onUpdate={
            onPmsSettingsUpdate as (
              pmsType: PmsType | null,
              pmsConfig: AppfolioConfig | null,
            ) => Promise<void>
          }
          onRefresh={onStructureRefresh}
          existingConfig={appfolioPmsConfig}
        />
      )}

      {/* ResMan setup dialog. Wraps the ResmanConfigForm; the form owns Cancel/Save
          buttons, we just provide a close X and the dialog frame. */}
      {structureUnits && (
        <Dialog
          open={showResmanSetup}
          onClose={() => setShowResmanSetup(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Set Up ResMan Export
            <IconButton
              aria-label="close"
              onClick={() => setShowResmanSetup(false)}
              size="small"
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, '& > .MuiCard-root': { mt: 0, mb: 0, boxShadow: 'none' } }}>
            <ResmanConfigForm
              pmsConfig={resmanPmsConfig}
              units={structureUnits}
              onUpdate={
                onPmsSettingsUpdate as (
                  pmsType: PmsType | null,
                  pmsConfig: ResmanConfig | null,
                ) => Promise<void>
              }
              onRefresh={() => {
                onStructureRefresh?.();
                setShowResmanSetup(false);
              }}
              onCancel={() => setShowResmanSetup(false)}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Upload Charges Confirmation Dialog */}
      <Dialog
        open={showUploadConfirm}
        onClose={() => !isUploading && setShowUploadConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Upload to Rent Manager
          <IconButton
            aria-label="close"
            onClick={() => setShowUploadConfirm(false)}
            disabled={isUploading}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            The following charges will be uploaded to Rent Manager:
          </Typography>
          {vacantCount > 0 && !loadingOccupants && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {vacantCount} unit{vacantCount !== 1 ? 's have' : ' has'} no active tenant in Rent
              Manager and will be skipped.
            </Alert>
          )}
          {midCycleSkipCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {midCycleSkipCount} unit{midCycleSkipCount !== 1 ? 's' : ''} had mid-cycle tenant
              turnover in SimpleSUB this period and will be skipped. Rent Manager charges one tenant
              per unit, but the prorated charges here need to be split across multiple tenants. Bill
              these manually in Rent Manager.
            </Alert>
          )}
          <TableContainer sx={{ maxHeight: 350 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Unit</TableCell>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...uploadPayloads]
                  .sort((a, b) => {
                    const unitA = enrichedUnits.find((u) => rmUnitPmsIds[u.id] === a.UnitID);
                    const unitB = enrichedUnits.find((u) => rmUnitPmsIds[u.id] === b.UnitID);
                    const nameA = unitPmsNames?.[unitA?.id ?? ''] ?? unitA?.name ?? '';
                    const nameB = unitPmsNames?.[unitB?.id ?? ''] ?? unitB?.name ?? '';
                    return naturalCompare(nameA, nameB);
                  })
                  .map((payload) => {
                    const ssUnit = enrichedUnits.find((u) => rmUnitPmsIds[u.id] === payload.UnitID);
                    const rmUnitName =
                      unitPmsNames?.[ssUnit?.id ?? ''] ?? `RM Unit ${payload.UnitID}`;
                    const isBillable = ssUnit?.is_billable ?? true;
                    const breakdown = ssUnit
                      ? buildUnitChargeBreakdown(bill, ssUnit, isBillable, persistedInvoiceContext)
                      : [];
                    const occupant = occupants[String(payload.UnitID)];
                    const isVacant = !loadingOccupants && occupant === null;
                    const isMidCycle = ssUnit ? midCycleUnitIds.has(ssUnit.id) : false;
                    const isSkipped = isVacant || isMidCycle;
                    const ssTenants = ssUnit ? ssTenantsByUnit[ssUnit.id] ?? [] : [];
                    return (
                      <TableRow key={payload.UnitID} sx={isSkipped ? { opacity: 0.5 } : undefined}>
                        <TableCell>
                          <Typography variant="body2">{rmUnitName}</Typography>
                          {ssUnit && ssUnit.name !== rmUnitName && (
                            <Typography variant="caption" color="textSecondary">
                              {ssUnit.name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {loadingOccupants ? (
                            <Typography variant="body2" color="textSecondary">
                              Loading...
                            </Typography>
                          ) : isMidCycle ? (
                            <>
                              <Typography variant="body2" color="error">
                                Multiple tenants this period
                              </Typography>
                              {ssTenants.length > 0 && (
                                <Typography variant="caption" color="textSecondary">
                                  SimpleSUB: {ssTenants.join(', ')}
                                </Typography>
                              )}
                            </>
                          ) : isVacant ? (
                            <>
                              <Typography variant="body2" color="error">
                                No active tenant in Rent Manager
                              </Typography>
                              {ssTenants.length > 0 && (
                                <Typography variant="caption" color="textSecondary">
                                  SimpleSUB: {ssTenants.join(', ')}
                                </Typography>
                              )}
                            </>
                          ) : occupant ? (
                            <Typography variant="body2">{occupant.Name}</Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              breakdown.length > 0 ? (
                                <Box>
                                  {breakdown.map((item, idx) => (
                                    <Box
                                      key={idx}
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        minWidth: 150,
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ mr: 2 }}>
                                        {item.label}:
                                      </Typography>
                                      <Typography variant="caption">
                                        ${formatNumber(item.amount, 2)}
                                      </Typography>
                                    </Box>
                                  ))}
                                  <Box
                                    sx={{
                                      borderTop: 1,
                                      borderColor: 'divider',
                                      mt: 0.5,
                                      pt: 0.5,
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 'bold', mr: 2 }}
                                    >
                                      Total:
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                      ${formatNumber(payload.Amount, 2)}
                                    </Typography>
                                  </Box>
                                </Box>
                              ) : (
                                ''
                              )
                            }
                            arrow
                            placement="left"
                          >
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                cursor: 'help',
                                borderBottom: '1px dotted rgba(0, 0, 0, 0.3)',
                                '&:hover': { borderBottomColor: 'primary.main' },
                              }}
                            >
                              ${formatNumber(payload.Amount, 2)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {/* Total row inside the table body to avoid double border */}
                <TableRow>
                  <TableCell colSpan={2} sx={{ fontWeight: 600, borderBottom: 'none' }}>
                    Total ({eligiblePayloads.length} unit
                    {eligiblePayloads.length !== 1 ? 's' : ''})
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>
                    $
                    {formatNumber(
                      eligiblePayloads.reduce((sum, p) => sum + p.Amount, 0),
                      2,
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {rmPmsConfig?.default_charge_type && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Charge type: {rmPmsConfig.default_charge_type}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <SWLoadingButtonOutlined
            onClick={() => setShowUploadConfirm(false)}
            disabled={isUploading}
          >
            Cancel
          </SWLoadingButtonOutlined>
          <SWLoadingButton
            onClick={handleConfirmUpload}
            loading={isUploading}
            disabled={loadingOccupants || eligiblePayloads.length === 0}
          >
            {isUploading
              ? 'Uploading...'
              : `Upload ${eligiblePayloads.length} Charge${
                  eligiblePayloads.length !== 1 ? 's' : ''
                }`}
          </SWLoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
